import { Button } from '@brightdrop/bd-ui';
import { useTranslations } from '@brightdrop/localization-client';
import { cx } from '@emotion/css';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { type ReactNode, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import usePageTitle from '../../hooks/usePageTitle';
import useStyles from './AlertMessage.styles';
import { getMobileAppUrlConfig } from './config/getAlertMessage.config';

const MESSAGES = {
  'common:alertMsg.appLogo': '{{brandName}} logo',
  'common:alertMsg.alertMsg': 'Alert message',
};
export interface AlertMessageProps {
  header?: string;
  body?: string;
  topic?: string;
  hideLogo?: boolean;
  imageContent?: ReactNode;
  showGraphic?: boolean;
  linkBtn?: string;
  onClick?: () => void;
  id?: string;
  wrapperClassName?: string;
}

const AlertMessage = ({
  header,
  body,
  topic,
  hideLogo = true,
  imageContent,
  showGraphic = false,
  linkBtn,
  onClick,
  id,
  wrapperClassName,
}: AlertMessageProps): JSX.Element => {
  usePageTitle(header);
  const theme = useTheme();
  const isMobileAppRequiredLayout = useMediaQuery(theme.breakpoints.down('lg'));
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { translations } = useTranslations(MESSAGES);
  const handleClicked = () => navigate('/');
  const getDownloadItems = useMemo(() => getMobileAppUrlConfig(), []);

  return (
    <div
      className={cx(
        classes.alertMessageWrapper,
        `topic_${topic ? topic : 'default'} ${
          showGraphic ? 'showGraphic' : 'noGraphic'
        }`,
        wrapperClassName
      )}
    >
      <div className={classes.alertMessageContainer}>
        <div className={cx(classes.alertMessageLeftCol, 'leftColumn')}>
          <Box className="alertWrapper">
            {!hideLogo && (
              <div
                data-testid={'alert-message-app-logo'}
                role="img"
                aria-label={translations['common:alertMsg.appLogo']}
                className={classes.logo}
              />
            )}
            <Box
              display="flex"
              alignSelf="center"
              alignItems="center"
              justifyContent="center"
              flexDirection="column"
              className={
                topic === 'mobileAppRequired'
                  ? cx(
                      classes.mobileAppRequiredContent,
                      'mobileAppRequiredStack'
                    )
                  : undefined
              }
            >
              {imageContent && (
                <div
                  className={classes.imageContentContainer}
                  data-testid={'alert-message-imageContent-container'}
                >
                  {imageContent}
                </div>
              )}
              {header && (
                <Box
                  data-testid={`${id}-alert-title`}
                  component="div"
                  className={cx(classes.alertTitle, 'alertTitleCopy')}
                >
                  <Typography
                    variant={
                      topic === 'mobileAppRequired' && isMobileAppRequiredLayout
                        ? 'display2_medium'
                        : 'display1_regular'
                    }
                  >
                    {header}
                  </Typography>
                </Box>
              )}
              {body && (
                <Box
                  aria-label={translations['common:alertMsg.alertMsg']}
                  component="div"
                  data-testid={`${id}-alert-message`}
                  className={
                    topic === 'mobileAppRequired'
                      ? classes.mobileAppRequiredBodyWrap
                      : undefined
                  }
                >
                  <Typography
                    variant={
                      topic === 'mobileAppRequired'
                        ? isMobileAppRequiredLayout
                          ? 'body_medium'
                          : 'header2_regular'
                        : 'body_regular'
                    }
                    className={cx(
                      classes.body,
                      'alertBody',
                      topic === 'mobileAppRequired' &&
                        classes.mobileAppRequiredBody
                    )}
                    gutterBottom={topic !== 'mobileAppRequired'}
                  >
                    {body}
                  </Typography>
                </Box>
              )}
              {!!topic && topic === 'mobileAppRequired' && (
                <Box
                  component="div"
                  className="appStoreLogos"
                  data-testid="app-store-link-container"
                >
                  {getDownloadItems.map((items) => (
                    <Box key={items.id}>
                      <Link
                        href={items.url}
                        data-testid={items.id}
                        target="_blank"
                      >
                        <Box>{items.img}</Box>
                      </Link>
                    </Box>
                  ))}
                </Box>
              )}
              {!!linkBtn && (
                <Box component="div">
                  <Button
                    id="alert-btn"
                    size="large"
                    margin="16px 0 0"
                    onClick={onClick || handleClicked}
                  >
                    {linkBtn}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </div>
        {!!showGraphic && (
          <div
            className={cx(classes.alertMessageRightCol, 'alertGraphicColumn')}
          >
            <div
              className="alertMessageGraphic"
              data-testid="alert-message-graphic"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertMessage;
